<template>
  <v-app>
    <!-- Drawerは常に v-app 直下に配置。内部の v-if で表示を制御 -->
    <navigation-drawer v-if="isReady" />

    <!-- v-main も常に配置し、中身の表示を isReady で切り替える -->
    <v-main>
      <v-container fluid v-if="isReady"> <!-- fluidを付けると横幅一杯に広がります -->
        <router-view />
      </v-container>

      <!-- ローディング表示を v-main の中央に配置 -->
      <v-overlay v-else :value="true">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-main>
  </v-app>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import NavigationDrawer from "@/components/organisms/common/NavigationDrawer.vue";
import { auth0Module } from "@/store/dataModule/auth0Module";
import { meModule } from "@/store/dataModule/meModule";
import LocalDataService from "@/service/LocalDataService";

export default defineComponent({
  name: "App",
  components: { NavigationDrawer },
  data() {
    return {
      isReady: false,
    };
  },

  async created() {
    // 1. Auth0の初期化（既存セッションからトークンを復元）
    await auth0Module.initClient();

    // 2. Auth0からの戻り(Callback)判定
    const params = new URLSearchParams(window.location.search);
    if (params.has("code") && params.has("state")) {
      try {
        // コールバック処理を実行し、トークン等を保持
        await auth0Module.handleAuth0();

        // URLからクエリパラメータ（code, state）を取り除く
        await this.$router.replace(this.$route.path);
      } catch (e) {
        console.error("Auth0 Callback Error:", e);
        auth0Module.signIn();
        return;
      }
    }

    // 3. 認証状態の確認と遷移制御
    // コールバック経由の場合も、通常アクセスの場合も、必ずここを通す
    await this.checkAuthentication();
    this.isReady = true;
  },

  computed: {
    // Vuex または LocalStorage にトークンがあるか
    tokenExists(): boolean {
      return !!auth0Module.accessToken || !!LocalDataService.getAccessToken();
    },
  },

  methods: {
    async checkAuthentication() {
      if (this.tokenExists) {
        try {
          // meAPIを叩く
          // ※ここで403が出る場合、meModule内のaxios設定が 'Bearer ' + token になっているか確認
          const response = await meModule.fetchMe().catch((err) => {
            // 401/403ならエラーとしてthrowして下のcatchへ
            if (err.response?.status === 401 || err.response?.status === 403) {
              throw err;
            }
            return null;
          });

          if (response && response.companies) {
            if (response.companies.length > 0) {
              const company = LocalDataService.getCompany();
              if (!company) {
                // 会社未選択なら選択画面へ
                if (this.$route.path !== "/select-company") {
                  this.$router.push("/select-company");
                }
              } else {
                // ログイン済みで /signin や / にいるならトップへ
                if (this.$route.path === "/signin" || this.$route.path === "/") {
                  this.goTop();
                }
              }
            } else {
              // 企業リストが空の場合
              alert("ログインできる企業が存在しません。管理者に問い合わせてください。");
            }
          } else {
            // レスポンスが不正、または新規ユーザーの場合
            const postRes = await meModule.postWithNoBody().catch(() => null);
            if (postRes) {
              this.$router.push("/select-company");
            }
          }
        } catch (error: any) {
          console.error("API Auth Error:", error);
          // トークンが無効（401/403）なら、一度クリアして再ログイン
          LocalDataService.clearAll();
          auth0Module.signIn();
        }
      } else {
        // 現在のURLに code と state（Auth0のログイン完了戻りパラメータ）があるか確認
        const params = new URLSearchParams(window.location.search);
        const isAuth0Callback = params.has("code") && params.has("state");

        // Auth0からの戻り処理中（createdのステップ2）「ではない」なら、
        // ログアウト直後であっても、直接アクセスであっても、即座にログインダイアログを起動する
        if (!isAuth0Callback) {
          auth0Module.signIn();
        }
      }
    },
    goTop() {
      if (this.$route.path !== "/invoice") {
        this.$router.push({ path: "/invoice" });
      }
    },
  },
});
</script>